import { ElementType } from "react";

import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import { Grid, Paper, Tooltip, Typography } from "@mui/material";
import { FormikProps } from "formik";

import { FormikCheckbox } from "@/common/components/fields/formik/formik-checkbox/FormikCheckbox";
import { useTranslation } from "@/translations";

interface ClientNotificationValues {
  notifyBySms: boolean;
  notifyByEmail: boolean;
}

interface Props<T extends ClientNotificationValues> {
  formik: FormikProps<T>;
  phone: string | null;
  email: string | null;
  /** True while the client is marked for removal: the header announces the cancellation notice. */
  cancellation: boolean;
}

/**
 * The client-notification section of the appointment dialogs: one checkbox card per channel,
 * each showing the client's stored contact data. A channel without stored contact data cannot be
 * selected — the address is replaced by a red hint, because the notification would fail.
 */
export function ClientNotificationSection<T extends ClientNotificationValues>({
  formik,
  phone,
  email,
  cancellation,
}: Props<T>) {
  const { t } = useTranslation();

  return (
    <>
      <Typography variant="subtitle2" color="text.secondary" mb={1}>
        {cancellation
          ? t("calendar.appointmentClient.notifyCancellationTitle")
          : t("calendar.appointmentClient.notifyTitle")}
      </Typography>
      <Grid container columnSpacing={2}>
        <Grid size={6}>
          <ChannelCard
            formik={formik}
            fieldName="notifyBySms"
            icon={SmsOutlinedIcon}
            title={t("calendar.appointmentClient.bySms")}
            contactValue={phone}
            missingText={t("calendar.appointmentClient.noPhone")}
            missingTooltip={t("calendar.appointmentClient.noPhoneTooltip")}
          />
        </Grid>
        <Grid size={6}>
          <ChannelCard
            formik={formik}
            fieldName="notifyByEmail"
            icon={MailOutlinedIcon}
            title={t("calendar.appointmentClient.byEmail")}
            contactValue={email}
            missingText={t("calendar.appointmentClient.noEmail")}
            missingTooltip={t("calendar.appointmentClient.noEmailTooltip")}
          />
        </Grid>
      </Grid>
    </>
  );
}

interface ChannelCardProps<T extends ClientNotificationValues> {
  formik: FormikProps<T>;
  fieldName: keyof ClientNotificationValues;
  icon: ElementType;
  title: string;
  contactValue: string | null;
  missingText: string;
  missingTooltip: string;
}

function ChannelCard<T extends ClientNotificationValues>({
  formik,
  fieldName,
  icon: Icon,
  title,
  contactValue,
  missingText,
  missingTooltip,
}: ChannelCardProps<T>) {
  const available = !!contactValue;
  const checked = formik.values[fieldName];

  const card = (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1, display: "flex", alignItems: "center", height: "100%" }}>
      <FormikCheckbox
        formik={formik}
        fieldName={fieldName}
        disabled={!available}
        size="small"
        slotProps={{ input: { "aria-label": title } }}
        // Without a label MUI's own label margins only offset the checkbox out of the card padding
        // and add a phantom gap before the icon.
        formControlLabelProps={{ sx: { m: 0 } }}
        // The checkbox's own padding is what separates it from the icon, so it is trimmed rather
        // than spaced with a flex gap — 4px keeps the hit area at 28px (WCAG 2.5.8 wants ≥ 24px).
        sx={{ p: 0.5 }}
      />
      {/* Tight to the checkbox, roomier towards the text — the gaps are not symmetric. The icon
          tracks the checkbox: primary while the channel is selected, greyed out otherwise. */}
      <Icon
        fontSize="small"
        sx={{ ml: 0.25, mr: 1.25 }}
        color={checked ? "primary" : available ? "action" : "disabled"}
      />
      <div>
        <Typography variant="body2" lineHeight={1.3} color={available ? "text.primary" : "text.disabled"}>
          {title}
        </Typography>
        <Typography
          variant="caption"
          component="div"
          lineHeight={1.3}
          color={available ? "text.secondary" : "error.main"}
        >
          {contactValue ?? missingText}
        </Typography>
      </div>
    </Paper>
  );

  // A disabled control cannot carry a tooltip itself, so the reason is attached to a wrapper.
  return available ? (
    card
  ) : (
    <Tooltip title={missingTooltip}>
      <span>{card}</span>
    </Tooltip>
  );
}
